/* eslint-disable @typescript-eslint/no-explicit-any */
import { AnyAction, Dispatch, AsyncThunkPayloadCreator } from '@reduxjs/toolkit';
import PQueue from 'p-queue';

import { ThunkAPI } from '../slices/rootReducer';
import API, { ApiRequest, SERVICES, HttpError, StatusTypes, CORE_SERVICES } from './api';
import { setAuthToken, updateUniqueSession } from '../slices/auth';
import { userLogout } from '../slices/auth/login.thunks';
import { IRefreshTokenResponse } from '../slices/auth/refreshToken.thunks';
import { AuthState, LoggingMode } from '../slices/auth/authState';
import { push } from 'connected-react-router';
import { refreshCognitoIdToken } from './cognito';

const queue = new PQueue({ concurrency: 1 });

type Headers = {
  [key: string]: string;
}

type AuthorizationConfig = {
  token: string | null;
  authenticated: boolean;
}

type RequestConfig = {
  accept?: string;
  contentType?: string;
}

type ActionCreators = string | AnyAction | ((...params: unknown[]) => AnyAction);

export type ApiCallerProps = {
  endpoint: string;
  authenticated: boolean;
  types?: [] | [ActionCreators[], ActionCreators[]];
  microservice: string;
  method: string;
  contentType?: string;
  accept?: string;
  cognitoToken?: string;
};

type RefreshTokenProps = {
  config: RequestInit;
  microservice: string;
  endpoint: string;
  dispatch: Dispatch;
  loggingMode: LoggingMode;
};

type CallApiProps<T> = {
  endpoint: string;
  authenticated: boolean;
  microservice: string;
  method: string;
  data?: T;
  contentType?: string;
  accept?: string;
  dispatch: Dispatch;
  authState: AuthState;
  cognitoToken?: string;
};

type ErrorMessage = {
  errorMessage: string;
}

export enum TokenTypes {
  Cognito = 'Cognito'
}

export type CallAPIError = {
  status: number;
  data: ErrorMessage;
}

type ErrorOrResponseProps = {
  error?: CallAPIError;
  response?: Response;
}

enum HttpClientErrors {
  ClientError = 400,
  UnauthorizedError = 401,
  PaymentRequired = 402,
  Forbidden = 403,
  NotFound = 404,
  Conflict = 409,
  Gone = 410,
  UnprocessableEntity = 422,
  TooMany = 429,
}

export const addTokenIfIsAuthenticated = (authenticated = false, token: string | null, config: RequestInit): RequestInit => {
  if (!authenticated && !token) {
    return config;
  }

  if (token) {
    return {
      ...config,
      headers: {
        'Authorization': token
      }
    };
  }
  return { ...config };
};

export const addContentType = (config: RequestInit, contentType = 'application/json'): RequestInit => {
  if (!contentType) {
    return config;
  }
  return {
    ...config,
    headers: { ...config.headers, 'Content-Type': contentType }
  };
};

export const addAccept = (config: RequestInit, accept?: string): RequestInit => {
  if (!accept) {
    return config;
  }
  return {
    ...config,
    headers: { ...config.headers, 'Accept': accept }
  };
};

const addHeaders = (config: RequestInit, { authenticated, token }: AuthorizationConfig, { contentType, accept }: RequestConfig): RequestInit => {
  let configWithHeaders = addTokenIfIsAuthenticated(authenticated, token, config);
  configWithHeaders = addContentType(configWithHeaders, contentType);
  configWithHeaders = addAccept(configWithHeaders, accept);
  return configWithHeaders;
};

export const addBody = (data: unknown, configWithHeaders: RequestInit): RequestInit => {
  if (!data) {
    return configWithHeaders;
  }
  const headers = configWithHeaders.headers as Headers;

  if (!headers['Content-Type']) {
    return {
      ...configWithHeaders,
      body: data as BodyInit
    };
  }
  return {
    ...configWithHeaders,
    body: JSON.stringify(data)
  };
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const parseResponse = (response: unknown) => {
  if (response instanceof HttpError) {
    return Promise.reject(response.message);
  }
  if (response instanceof Error) {
    return Promise.reject(response.message);
  }
  if (typeof response === 'string') {
    return JSON.parse(response);
  }
  return response;
};

export const refreshToken = async ({ dispatch, config, microservice, endpoint, loggingMode }: RefreshTokenProps): Promise<Response> => {
  let refreshTokenConfig = config;
  const requestData: ApiRequest<void> = {
    service: SERVICES.AUTH_SERVICE,
    url: 'refresh'
  };

  if (loggingMode === LoggingMode.Social) {
    const newCognitoToken = await refreshCognitoIdToken();
    dispatch(setAuthToken(newCognitoToken));
    requestData.config = {
      ...config,
      headers: {
        ...config.headers,
        Authorization: newCognitoToken
      }
    };
  }
  try {
    const response = await API.post<void, IRefreshTokenResponse>(requestData);
    const newToken = response.token;
    dispatch(setAuthToken(newToken));
    refreshTokenConfig = {
      ...config,
      headers: {
        ...config.headers,
        Authorization: newToken
      }
    };
  } catch (err: any) {
    // User has logged in on a different device
    if (err.statusCode === HttpClientErrors.Forbidden) {
      dispatch(updateUniqueSession());
    } else {
      dispatch(userLogout() as never);
    }
  }
  return API.http<Response>(microservice, endpoint, refreshTokenConfig);
};

const callApi = async <T>({
  endpoint,
  authenticated,
  microservice,
  method,
  data,
  contentType,
  accept,
  dispatch,
  authState,
  cognitoToken
}: CallApiProps<T>) => {
  let token = '';

  if (authenticated && authState) {
    token = authState.token;
  }

  if (!authenticated && cognitoToken) {
    token = cognitoToken;
  }

  let isCoreService = false;
  Object.values(CORE_SERVICES).forEach((serviceUrl) => {
    if (microservice.includes(serviceUrl)) {
      isCoreService = true;
    }
  });
  let headers: RequestInit = {
    method,
    'mode': 'cors'
  };

  if (isCoreService) {
    headers = {
      ...headers,
      'credentials': undefined
    };
  }

  const configWithHeaders = addHeaders(headers, { authenticated, token }, { contentType, accept });
  const config = addBody(data, configWithHeaders);

  try {
    const response = await API.http<Response>(microservice, endpoint, config);
    return parseResponse(response);
  } catch(err: any) {
    if ( err.statusCode === HttpClientErrors.Gone ) {
      await dispatch(userLogout() as never);
      dispatch(push('/login'));
      return parseResponse(err);
    }

    if (err.statusCode === HttpClientErrors.PaymentRequired && err.status === StatusTypes.ExpiredAccount) {
      dispatch(push('/expired-account'));
      return parseResponse(err);
    }

    if (err.statusCode === HttpClientErrors.PaymentRequired && err.status === StatusTypes.UpgradeInProgress) {
      dispatch(push('/upgrade-in-progress'));
      return parseResponse(err);
    }

    if ((HttpClientErrors.Forbidden === err.statusCode && (StatusTypes.UserInactive === err.status || StatusTypes.SsoOnlyEnabled)) || err.statusCode === HttpClientErrors.UnprocessableEntity) {
      return Promise.reject(err);
    }

    if ((err.statusCode !== HttpClientErrors.UnauthorizedError && err.statusCode !== HttpClientErrors.Forbidden) || !authenticated) {
      return parseResponse(err);
    }

    let response: Response;
    if (err.statusCode === HttpClientErrors.UnauthorizedError) {
      response = await queue.add(() => refreshToken({
        dispatch,
        loggingMode: authState.loggingMode,
        config,
        microservice,
        endpoint
      }));
      return parseResponse(response);
    }
  }
};

export const executeActions = (dispatch: Dispatch, { error, response }: ErrorOrResponseProps, types: ActionCreators[] = []): void => {
  const actionTypes = [...types];
  actionTypes.forEach((actionType) => {
    if (typeof actionType === 'function') {
      if (error && error.data && error.data.errorMessage) {
        const { type, ...extras } = actionType(error.data.errorMessage);
        return dispatch({ error, response, ...extras, type });
      }
      const { type, ...extras } = actionType(error);
      return dispatch({ error, response, ...extras, type });
    }

    if (typeof actionType === 'object') {
      return dispatch({ error, response, ...actionType });
    }

    return dispatch({
      response,
      error,
      type: actionType
    });
  });
};

export function basicApiAsyncThunkCreator<R, T>({
  endpoint,
  authenticated,
  microservice,
  method,
  contentType = 'application/json',
  accept,
  cognitoToken
}: ApiCallerProps): AsyncThunkPayloadCreator<R, T, ThunkAPI> {
  return (data: T, thunk) => {
    const authState = thunk.getState().auth;
    return callApi<T>({
      endpoint,
      authenticated,
      microservice,
      method,
      data,
      contentType,
      accept,
      dispatch: thunk.dispatch,
      authState,
      cognitoToken
    });
  };
}

export default function apiAsyncThunkCreator<R, T>({
  types = [],
  ...apiParams
}: ApiCallerProps): AsyncThunkPayloadCreator<R, T, ThunkAPI> {
  return async (data: T, thunk) => {
    const [successTypes, errorTypes] = types;
    try {
      const response = await basicApiAsyncThunkCreator<R, T>(apiParams)(data, thunk);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const successResponse = response as any;
      executeActions(thunk.dispatch, { response: successResponse }, successTypes);
      return response;
    } catch (error) {
      executeActions(thunk.dispatch, { error: error as CallAPIError }, errorTypes);
      return thunk.rejectWithValue(error);
    }
  };
}
